import { useCallback, useEffect, useState } from "react"
import { useScroll } from "framer-motion"

/**
 * Scroll-linked parallax offset for the collage / grid-row images.
 *
 * Returns `{ measuredRef, scrollPercentage }`. Attach `measuredRef` to the element whose
 * position drives the effect; `scrollPercentage` runs roughly 0 → 1 as that element travels
 * from the bottom of the viewport to the top.
 *
 * This replaces five copies of the same effect (grid-row + four collage slices), which shared
 * two defects:
 *
 * 1. **The offset was never seeded.** `scrollY.on("change")` only fires once the user
 *    scrolls, so an image sat at `scrollPercentage = 0` — an offset unrelated to where it
 *    actually was on the page — until the first scroll event. That is what made the resting
 *    offset look like it depended on how you arrived at a scroll position: before any scroll
 *    you saw the uninitialised value, after scrolling back you saw the real one.
 *
 * 2. **It measured through ancestor transforms.** `getBoundingClientRect()` includes every
 *    transform applied above the element, and these images mount inside a framer-motion
 *    enter animation (`y: 48 → 0`). Measuring during that animation captured a transient
 *    position and then froze, because nothing re-measures while the page is not scrolling.
 *
 * Walking `offsetTop`/`offsetParent` fixes both: it reports the element's **layout**
 * position, which CSS transforms do not affect, so the result is a pure function of the
 * scroll position and is stable regardless of animation state. Once the enter animation
 * settles at `y: 0` this agrees exactly with the old `getBoundingClientRect()` reading.
 */

// Distance from the top of the document, ignoring any ancestor transforms.
const documentTop = node => {
  let top = 0
  for (let el = node; el; el = el.offsetParent) top += el.offsetTop
  return top
}

const viewportHeight = () =>
  window.innerHeight ||
  document.documentElement.clientHeight ||
  document.body.clientHeight

const isLegacyIE = () =>
  navigator.userAgent.indexOf("MSIE") !== -1 ||
  navigator.appVersion.indexOf("Trident/") > -1

export const useParallax = () => {
  const { scrollY } = useScroll()
  const [scrollPercentage, setScrollPercentage] = useState(0)
  const [wrapper, setWrapper] = useState(undefined)

  const measuredRef = useCallback(node => {
    setWrapper(node)
  }, [])

  useEffect(() => {
    if (!wrapper) return

    // On IE, force show state immediately to avoid performance issues
    if (isLegacyIE()) {
      setScrollPercentage(0.5)
      return
    }

    const update = () => {
      const top = documentTop(wrapper) - window.scrollY
      setScrollPercentage(-top / viewportHeight() + 1)
    }

    update()

    const unsubscribe = scrollY.on("change", update)
    window.addEventListener("resize", update)

    // Re-measure when the document reflows. Seeding once is not enough: images above the
    // fold finish loading after mount and push everything below them down (on the home page
    // the hero alone moves the first grid row by a full viewport height), and nothing
    // re-measures while the page is not being scrolled. This also covers webfont swap.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update)
    if (observer) observer.observe(document.body)

    return () => {
      unsubscribe()
      window.removeEventListener("resize", update)
      if (observer) observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapper])

  return { measuredRef, scrollPercentage }
}

export default useParallax
